// src/features/trainings/TrainingForm.jsx - ACTUALIZADO CON SELECTOR DE INSTRUCTORES
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Form, Button, Card, Row, Col, Spinner, InputGroup } from 'react-bootstrap';
import { FaSave, FaTimes } from 'react-icons/fa';
import dayjs from 'dayjs';

// Componentes
import CustomSnackbar from '../../components/CustomSnackbar';
import AssignParticipantsComponent from './components/AssignParticipantsComponent';

// Servicios y constantes
import TrainingsService from './TrainingsService';
import { TIPOS_CAPACITACIONES } from '../../utils/constants';

// Constantes para la selección de instructores
const NEW_INSTRUCTOR_OPTION_VALUE = '---CREATE_NEW---';
const NEW_INSTRUCTOR_LABEL = 'Crear un nuevo instructor...';

/**
 * Componente para crear o editar una capacitación
 */
function TrainingForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Estados simples y directos
  const [loading, setLoading] = useState(false); // Estado principal de loading
  const [formLoading, setFormLoading] = useState(id ? true : false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '' });
  
  // Estados para instructores
  const [instructorOptions, setInstructorOptions] = useState([]);
  const [loadingInstructors, setLoadingInstructors] = useState(true);
  const [isCreatingNewInstructor, setIsCreatingNewInstructor] = useState(false);
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    type: '',
    date: dayjs().format('YYYY-MM-DD'),
    instructor: '',
    topic: '',
    observations: '',
    personsIds: [],
    persons: []
  });

  // Cargar instructores disponibles
  useEffect(() => {
    //console.log('🎯 useEffect de instructores ejecutándose...');
  //console.log('📋 TrainingsService:', TrainingsService);
  //console.log('📋 getInstructors método:', typeof TrainingsService.getInstructors);
    const fetchInstructors = async () => {
      try {
        setLoadingInstructors(true);
        const names = await TrainingsService.getInstructors();
        //console.log("Instructores recibidos:", names); // Para debug
        
        if (Array.isArray(names)) {
          let nameList = [];
          
          // Procesar cada elemento de manera flexible
          names.forEach(item => {
            let name = null;
            
            if (Array.isArray(item)) {
              // Si es array, intentar tomar el segundo elemento
              name = item[1];
            } else if (typeof item === 'string') {
              // Si es string directamente
              name = item;
            } else if (item && typeof item === 'object' && item.instructor) {
              // Si es objeto con propiedad instructor
              name = item.instructor;
            }
            
            // Validar que el nombre sea válido antes de agregarlo
            if (name && typeof name === 'string' && name.trim() !== '') {
              nameList.push(name.trim());
            }
          });
          
          // Eliminar duplicados
          nameList = [...new Set(nameList)];
          
          //console.log("Lista de instructores procesada:", nameList); // Para debug
          setInstructorOptions(nameList);
          
          // Si estamos editando y el instructor actual no está en la lista, activar modo creación
          if (id && formData.instructor && !nameList.includes(formData.instructor)) {
            setIsCreatingNewInstructor(true);
          }
        } else {
          console.error("La respuesta no es un array:", names);
          setInstructorOptions([]);
        }
      } catch (error) {
        console.error("Error al cargar instructores:", error);
        setInstructorOptions([]);
      } finally {
        setLoadingInstructors(false);
      }
    };
    fetchInstructors();
  }, [id, formData.instructor]);

  // Cargar datos si estamos en modo edición
  useEffect(() => {
    if (id) {
      loadTraining(id);
    }
  }, [id]);

  // Función para cargar datos de la capacitación
  const loadTraining = async (trainingId) => {
    try {
      setFormLoading(true);
      const training = await TrainingsService.getTraining(trainingId);
      
      // Formatear fecha para el input de tipo date
      setFormData({
        ...training,
        date: training.date ? dayjs(training.date).format('YYYY-MM-DD') : '',
        personsIds: training.persons ? training.persons.map(person => person.id) : []
      });
    } catch (error) {
      console.error('Error al cargar datos de la capacitación:', error);
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Error al cargar los datos de la capacitación'
      });
    } finally {
      setFormLoading(false);
    }
  };

  // Manejar selección de instructor
  const handleInstructorSelection = (e) => {
    const { value } = e.target;
    if (value === NEW_INSTRUCTOR_OPTION_VALUE) {
      // Primera alerta al seleccionar la opción de crear
      window.alert("Verifique que el instructor que requiere no está contemplado en la lista");
      
      setIsCreatingNewInstructor(true);
      setFormData(prev => ({ ...prev, instructor: '' }));
    } else {
      setIsCreatingNewInstructor(false);
      setFormData(prev => ({ ...prev, instructor: value }));
    }
    if (errors.instructor) {
      setErrors(prev => ({ ...prev, instructor: false }));
    }
  };

  // Manejar cambios en los inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error si existe
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  // Abrir modal para asignar participantes
  const handleOpenParticipantsModal = () => {
    setShowParticipantsModal(true);
  };

  // Cerrar modal de participantes
  const handleCloseParticipantsModal = () => {
    setShowParticipantsModal(false);
  };

  // Manejar cuando se guardan los participantes seleccionados
  const handleParticipantsSaved = (selectedPersons) => {
    setFormData(prev => ({
      ...prev,
      persons: selectedPersons,
      personsIds: selectedPersons.map(person => person.id)
    }));
    setShowParticipantsModal(false);
  };

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.type) newErrors.type = true;
    if (!formData.date) newErrors.date = true;
    if (!formData.instructor.trim()) newErrors.instructor = true;
    if (!formData.topic) newErrors.topic = true;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar envío del formulario - SOLUCIÓN SIMPLE
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // BLOQUEO INMEDIATO - Esta es la clave
    if (loading) {
      console.warn('Ya se está enviando, ignorando clic');
      return;
    }
    
    if (!validateForm()) {
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    if (isCreatingNewInstructor) {
      const instructorToCreate = formData.instructor.trim();
      
      // ✅ VALIDACIÓN ADICIONAL: Verificar si el instructor ya existe
      const normalizedNewInstructor = instructorToCreate.toLowerCase();
      const existingInstructor = instructorOptions.find(existing => 
        existing.toLowerCase() === normalizedNewInstructor
      );
      
      if (existingInstructor) {
        window.alert(`El instructor "${existingInstructor}" ya existe en la lista. Por favor selecciónelo del desplegable.`);
        setIsCreatingNewInstructor(false);
        setFormData(prev => ({ ...prev, instructor: existingInstructor }));
        return;
      }
      
      // Segunda alerta de confirmación antes de enviar
      const confirmCreation = window.confirm("¿Está seguro que desea crear un nuevo instructor?");
      if (!confirmCreation) {
        return; // Detener si el usuario cancela
      }
    }
    
    try {
      // Bloquear INMEDIATAMENTE antes de cualquier operación async
      setLoading(true);
      
      //console.log('Enviando capacitación...', formData);
      
      // Preparar datos con instructor limpio
      const dataToSave = {
        ...formData,
        instructor: formData.instructor.trim() // Limpiar espacios en blanco
      };
      
      if (id) {
        // Actualizar capacitación existente
        await TrainingsService.updateTraining(dataToSave, id);
        setSnackbar({
          open: true,
          type: 'success',
          message: 'Capacitación actualizada correctamente'
        });
      } else {
        // Crear nueva capacitación
        await TrainingsService.createTraining(dataToSave);
        setSnackbar({
          open: true,
          type: 'success',
          message: 'Capacitación creada correctamente'
        });
      }
      
      // Redireccionar después de mostrar el mensaje
      setTimeout(() => {
        navigate('/trainings');
      }, 1500);
      
    } catch (error) {
      console.error('Error al guardar la capacitación:', error);
      setSnackbar({
        open: true,
        type: 'error',
        message: `Error al ${id ? 'actualizar' : 'crear'} la capacitación: ${error.message || 'Error desconocido'}`
      });
      
      // IMPORTANTE: Resetear loading en caso de error
      setLoading(false);
    }
    // Nota: NO reseteamos loading en caso de éxito porque redirigimos
  };

  // Cancelar y volver a la lista
  const handleCancel = () => {
    navigate('/trainings');
  };

  // Cerrar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Si está cargando los datos, mostrar spinner
  if (formLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="my-3">
      <Card>
        <Card.Header className="bg-primary text-white text-center">
          <h5 className="m-0">{id ? 'Editar' : 'Agregar nueva'} Capacitación</h5>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Capacitación*</Form.Label>
                  <Form.Select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    isInvalid={errors.type}
                    disabled={loading}
                    required
                  >
                    <option value="">Seleccione un tipo</option>
                    {TIPOS_CAPACITACIONES.map(tipo => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.descripcion}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    El tipo de capacitación es requerido
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha*</Form.Label>
                  <Form.Control
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    isInvalid={errors.date}
                    disabled={loading}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    La fecha es requerida
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Instructor*</Form.Label>
                  {isCreatingNewInstructor ? (
                    <InputGroup>
                      <Form.Control
                        type="text"
                        name="instructor"
                        value={formData.instructor}
                        onChange={handleInputChange}
                        isInvalid={errors.instructor}
                        placeholder="Escriba el nuevo instructor"
                        disabled={loading}
                        autoFocus
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          setIsCreatingNewInstructor(false);
                          setFormData(prev => ({ ...prev, instructor: '' }));
                        }}
                        disabled={loading}
                      >
                        Cancelar
                      </Button>
                    </InputGroup>
                  ) : (
                    <Form.Select
                      name="instructor"
                      value={formData.instructor}
                      onChange={handleInstructorSelection}
                      isInvalid={errors.instructor}
                      disabled={loadingInstructors || loading}
                      required
                    >
                      <option value="">{loadingInstructors ? 'Cargando opciones...' : 'Seleccione de la lista...'}</option>
                      {instructorOptions.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                      <option value={NEW_INSTRUCTOR_OPTION_VALUE} style={{ fontStyle: 'italic', color: '#0d6efd' }}>
                        {NEW_INSTRUCTOR_LABEL}
                      </option>
                    </Form.Select>
                  )}
                  {errors.instructor && (
                    <Form.Control.Feedback type="invalid">
                      El instructor es requerido
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Tema*</Form.Label>
                  <Form.Control
                    type="text"
                    name="topic"
                    value={formData.topic}
                    onChange={handleInputChange}
                    isInvalid={errors.topic}
                    disabled={loading}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    El tema es requerido
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Observaciones</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="observations"
                    value={formData.observations}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row className="mb-3">
              <Col md={12}>
                <Card className="bg-light">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="mb-0">Participantes</h5>
                        <p className="text-muted mb-0">
                          {formData.persons.length} participantes asignados
                        </p>
                      </div>
                      <Button 
                        variant="primary" 
                        size="sm"
                        onClick={handleOpenParticipantsModal}
                        disabled={loading}
                        type="button"
                      >
                        {formData.persons.length > 0 ? 'Modificar Participantes' : 'Agregar Participantes'}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="secondary"
                onClick={handleCancel}
                className="me-2"
                disabled={loading}
                type="button"
              >
                <FaTimes className="me-1" /> Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={loading} // BLOQUEO SIMPLE Y EFECTIVO
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-1"
                    />
                    Guardando...
                  </>
                ) : (
                  <>
                    <FaSave className="me-1" /> {id ? 'Guardar Cambios' : 'Guardar'}
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
      
      {/* Modal para asignar participantes */}
      {showParticipantsModal && (
        <AssignParticipantsComponent
          show={showParticipantsModal}
          onHide={handleCloseParticipantsModal}
          onSave={handleParticipantsSaved}
          selectedPersons={formData.persons}
        />
      )}
      
      {/* CustomSnackbar simple */}
      <CustomSnackbar
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={handleCloseSnackbar}
      />
    </Container>
  );
}

export default TrainingForm;